import React, { useState, useEffect, useCallback } from 'react';
import {
  Apple,
  Plus,
  ChevronLeft,
  ChevronRight,
  Droplets,
  Flame,
  Loader2,
  X,
  Save,
} from 'lucide-react';
import { getUser } from '../../utils/helpers';
import { NODE_BACKEND_URL } from '../../utils/constant';

export default function NutritionDiaryPage() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [waterIntake, setWaterIntake] = useState(0);
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAddMeal, setShowAddMeal] = useState(false);

  const [newMeal, setNewMeal] = useState({
    name: 'Breakfast',
    time: '',
    calories: '',
    protein: '',
    carbs: '',
    fats: '',
    items: '',
  });

  const user = getUser();
  const userId = user?.id || user?._id || 'anonymous';

  const targets = { calories: 2500, protein: 180, carbs: 280, fats: 80 };
  const mealTypes = ['Breakfast', 'Lunch', 'Snack', 'Dinner', 'Pre-Workout', 'Post-Workout'];

  const formatDateKey = (date) => date.toISOString().split('T')[0];

  const formatDate = (date) =>
    date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const loadNutrition = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dateKey = formatDateKey(selectedDate);
      const response = await fetch(
        `${NODE_BACKEND_URL}/api/strength/user/${userId}/nutrition?date=${dateKey}`
      );
      if (!response.ok) throw new Error('Failed to load nutrition data');
      const data = await response.json();
      const entries = data.result || [];
      setMeals(entries.filter((e) => e.type === 'meal' || e.items));
      setWaterIntake(entries.find((e) => e.type === 'water')?.glasses || 0);
    } catch (err) {
      console.error('[Nutrition] Load error:', err);
      setError('Failed to load nutrition data');
      setMeals([]);
      setWaterIntake(0);
    } finally {
      setLoading(false);
    }
  }, [userId, selectedDate]);

  useEffect(() => { loadNutrition(); }, [loadNutrition]);

  const current = meals.reduce(
    (totals, meal) => ({
      calories: totals.calories + (Number(meal.calories) || 0),
      protein: totals.protein + (Number(meal.protein) || 0),
      carbs: totals.carbs + (Number(meal.carbs) || 0),
      fats: totals.fats + (Number(meal.fats) || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fats: 0 }
  );

  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  const handleAddMeal = async () => {
    if (!newMeal.name || !newMeal.calories) return;
    setSaving(true);
    setError(null);
    try {
      const entry = {
        type: 'meal',
        date: formatDateKey(selectedDate),
        name: newMeal.name,
        time: newMeal.time || new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        calories: Number(newMeal.calories) || 0,
        protein: Number(newMeal.protein) || 0,
        carbs: Number(newMeal.carbs) || 0,
        fats: Number(newMeal.fats) || 0,
        items: newMeal.items ? newMeal.items.split(',').map((i) => i.trim()).filter(Boolean) : [],
      };
      const response = await fetch(`${NODE_BACKEND_URL}/api/strength/user/${userId}/nutrition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      });
      if (!response.ok) throw new Error('Failed to save meal');
      const data = await response.json();
      setMeals((prev) => [...prev, data.result]);
      setNewMeal({ name: 'Breakfast', time: '', calories: '', protein: '', carbs: '', fats: '', items: '' });
      setShowAddMeal(false);
    } catch (err) {
      console.error('[Nutrition] Save meal error:', err);
      setError('Failed to save meal. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleWaterChange = async (newGlasses) => {
    setWaterIntake(newGlasses);
    try {
      await fetch(`${NODE_BACKEND_URL}/api/strength/user/${userId}/nutrition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'water', date: formatDateKey(selectedDate), glasses: newGlasses }),
      });
    } catch (err) {
      console.error('[Nutrition] Save water error:', err);
    }
  };

  const MacroBar = ({ label, current: cur, target, color }) => {
    const pct = Math.min((cur / target) * 100, 100);
    return (
      <div className="space-y-1">
        <div className="flex justify-between items-baseline">
          <span className="text-[10px] text-zinc-400">{label}</span>
          <span className="text-[10px] font-semibold text-white">{cur}g / {target}g</span>
        </div>
        <div className="h-[3px] rounded-full bg-[#1A1A1A] overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-full bg-black text-white p-3 pb-16">

      {/* Header + Date nav */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Apple size={14} className="text-amber-400" />
          <div>
            <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600">Nutrition</p>
            <h1 className="text-[15px] font-semibold tracking-tight">Diary</h1>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => changeDate(-1)}
            className="p-1.5 rounded-lg bg-white/[0.04] border border-[#1F1F1F]"
          >
            <ChevronLeft size={13} />
          </button>
          <span className="text-[10px] text-zinc-400 px-2 whitespace-nowrap">{formatDate(selectedDate)}</span>
          <button
            onClick={() => changeDate(1)}
            className="p-1.5 rounded-lg bg-white/[0.04] border border-[#1F1F1F]"
          >
            <ChevronRight size={13} />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-200 mb-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={20} className="text-amber-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* Calories + Macros */}
          <div className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A] px-4 py-3 mb-3">
            <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 mb-2">Daily Summary</p>
            <div className="flex items-baseline justify-between mb-3">
              <span className="text-[28px] font-bold leading-none">{current.calories}</span>
              <span className="text-[10px] text-zinc-500">/ {targets.calories} cal</span>
            </div>
            <div className="h-[3px] rounded-full bg-[#1A1A1A] overflow-hidden mb-3">
              <div
                className="h-full rounded-full bg-amber-400"
                style={{ width: `${Math.min((current.calories / targets.calories) * 100, 100)}%`, transition: 'width 0.8s ease' }}
              />
            </div>
            <div className="space-y-2.5">
              <MacroBar label="Protein" current={current.protein} target={targets.protein} color="#3b82f6" />
              <MacroBar label="Carbs" current={current.carbs} target={targets.carbs} color="#f59e0b" />
              <MacroBar label="Fats" current={current.fats} target={targets.fats} color="#ef4444" />
            </div>
          </div>

          {/* Water */}
          <div className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A] px-4 py-3 mb-3">
            <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 mb-2">Water Intake</p>
            <div className="flex items-center gap-1.5 flex-wrap">
              {[...Array(8)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleWaterChange(index + 1)}
                  className={`h-9 w-9 rounded-xl flex items-center justify-center transition-all border ${
                    index < waterIntake
                      ? 'bg-amber-400/20 border-amber-400/60'
                      : 'bg-white/[0.02] border-[#1F1F1F]'
                  }`}
                >
                  <Droplets size={14} className={index < waterIntake ? 'text-amber-400' : 'text-zinc-600'} />
                </button>
              ))}
            </div>
            <p className="text-[10px] text-zinc-600 mt-2">{waterIntake} / 8 glasses (64 oz)</p>
          </div>

          {/* Meals */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600">
                Meals {meals.length > 0 && `(${meals.length})`}
              </p>
              <button
                onClick={() => setShowAddMeal(!showAddMeal)}
                className="inline-flex items-center gap-1 rounded-full border border-white/20 px-2 py-1 text-[10px] text-zinc-300 hover:text-white"
              >
                {showAddMeal ? <X size={10} /> : <Plus size={10} />}
                {showAddMeal ? 'Cancel' : 'Add'}
              </button>
            </div>

            {showAddMeal && (
              <div className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A] px-4 py-3 mb-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[9px] text-zinc-500 mb-1">Meal Type</p>
                    <select
                      value={newMeal.name}
                      onChange={(e) => setNewMeal((prev) => ({ ...prev, name: e.target.value }))}
                      className="w-full px-2 py-1.5 bg-white/[0.03] border border-[#1F1F1F] rounded-xl text-[11px] text-white focus:outline-none focus:border-amber-400"
                    >
                      {mealTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-[9px] text-zinc-500 mb-1">Time</p>
                    <input
                      type="text"
                      value={newMeal.time}
                      onChange={(e) => setNewMeal((prev) => ({ ...prev, time: e.target.value }))}
                      placeholder="7:30 AM"
                      className="w-full px-2 py-1.5 bg-white/[0.03] border border-[#1F1F1F] rounded-xl text-[11px] text-white placeholder-zinc-600 focus:outline-none focus:border-amber-400"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {[['Calories', 'calories'], ['Protein', 'protein'], ['Carbs', 'carbs'], ['Fats', 'fats']].map(([label, key]) => (
                    <div key={key}>
                      <p className="text-[9px] text-zinc-500 mb-1">{label}</p>
                      <input
                        type="number"
                        value={newMeal[key]}
                        onChange={(e) => setNewMeal((prev) => ({ ...prev, [key]: e.target.value }))}
                        placeholder="0"
                        className="w-full px-2 py-1.5 bg-white/[0.03] border border-[#1F1F1F] rounded-xl text-[11px] text-white placeholder-zinc-600 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-[9px] text-zinc-500 mb-1">Food Items (comma-separated)</p>
                  <input
                    type="text"
                    value={newMeal.items}
                    onChange={(e) => setNewMeal((prev) => ({ ...prev, items: e.target.value }))}
                    placeholder="Chicken, rice, vegetables"
                    className="w-full px-2 py-1.5 bg-white/[0.03] border border-[#1F1F1F] rounded-xl text-[11px] text-white placeholder-zinc-600 focus:outline-none focus:border-amber-400"
                  />
                </div>
                <button
                  onClick={handleAddMeal}
                  disabled={saving || !newMeal.calories}
                  className="inline-flex w-full items-center justify-center gap-1 rounded-xl border border-amber-400/50 bg-amber-500/15 py-2 text-[11px] font-semibold text-amber-200 disabled:opacity-45"
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {saving ? 'Saving...' : 'Save Meal'}
                </button>
              </div>
            )}

            {meals.length === 0 ? (
              <div className="text-center py-10 text-zinc-600">
                <Apple size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-[11px]">No meals logged today.</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {meals.map((meal, index) => (
                  <div key={meal.id || index} className="rounded-xl border border-[#1F1F1F] bg-[#0A0A0A] px-3 py-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <p className="text-[12px] text-zinc-100">{meal.name}</p>
                        <p className="text-[10px] text-zinc-600">{meal.time}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Flame size={12} className="text-orange-500" />
                        <span className="text-[12px] font-semibold">{meal.calories}</span>
                        <span className="text-[10px] text-zinc-500">cal</span>
                      </div>
                    </div>
                    {(meal.protein || meal.carbs || meal.fats) && (
                      <div className="flex gap-3 text-[10px] text-zinc-500">
                        {meal.protein > 0 && <span>P: {meal.protein}g</span>}
                        {meal.carbs > 0 && <span>C: {meal.carbs}g</span>}
                        {meal.fats > 0 && <span>F: {meal.fats}g</span>}
                      </div>
                    )}
                    {meal.items?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {meal.items.map((item, idx) => (
                          <span key={idx} className="px-1.5 py-0.5 bg-white/[0.04] rounded-md text-[10px] text-zinc-400">{item}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
